/**
 * QuotePreview — Master prompt v3.0 dynamic link parser surface.
 *
 * Renders a single live quote card from the response of
 * POST /api/listings/parse-and-price-link. Only ever shows
 * `pricingDetails.deliveredPrice` and the `productDetails` surface; the
 * customer never sees the vendor base cost, the 20% concierge margin, or
 * the regional duty rate (those are stripped by the server before the
 * payload reaches the browser).
 *
 *   <QuotePreview
 *     api={API}
 *     targetExternalUrl={pastedLink}
 *     destinationRegion={destination}
 *     onClose={() => setOpen(false)}
 *   />
 *
 * Behaviours required by the spec:
 *   - "Pristine frontend checkout page" — the card lays out cleanly on
 *     every viewport (max-w-2xl mx-auto, responsive grid).
 *   - "Instantly fallback to luxury-placeholder.png" — on extractor
 *     failure or captcha block, the card swaps to the brand placeholder
 *     and surfaces a "Submit for manual validation" prompt.
 *   - "Brand sanity" — failure mode never displays a broken image icon
 *     or raw error stack; the placeholder + soft copy always renders.
 */

import { useEffect, useState } from "react";
import axios from "axios";
import {
  CHEEP_HOSTED_LUXURY_PLACEHOLDER,
  CHEEP_LUXURY_PLACEHOLDER_DATA_URI,
} from "../lib/productImageAssets";

const PLACEHOLDER_FALLBACKS = [
  CHEEP_HOSTED_LUXURY_PLACEHOLDER,
  CHEEP_LUXURY_PLACEHOLDER_DATA_URI,
];

function PlaceholderImage({ alt }) {
  const [stage, setStage] = useState(0);
  const src = PLACEHOLDER_FALLBACKS[stage] || CHEEP_LUXURY_PLACEHOLDER_DATA_URI;
  return (
    <img
      src={src}
      alt={alt || "CheepSweepz luxury concierge import"}
      className="w-full h-full object-contain p-4"
      loading="lazy"
      decoding="async"
      referrerPolicy="no-referrer"
      onError={() => {
        if (stage + 1 < PLACEHOLDER_FALLBACKS.length) setStage(stage + 1);
      }}
    />
  );
}

export default function QuotePreview({
  api,
  targetExternalUrl,
  destinationRegion = "BB",
  currency = "USD",
  onClose,
  onCommit,
}) {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [manualValidationRequired, setManualValidationRequired] = useState(false);
  const [quote, setQuote] = useState(null);
  const [imgFallbackStage, setImgFallbackStage] = useState(0);

  useEffect(() => {
    if (!targetExternalUrl) return;
    let cancelled = false;
    setLoading(true);
    setError(null);
    setManualValidationRequired(false);
    setQuote(null);
    axios
      .post(
        `${api}/listings/parse-and-price-link`,
        { targetExternalUrl, destinationRegion },
        { timeout: 30000, headers: { "Content-Type": "application/json" } }
      )
      .then((res) => {
        if (cancelled) return;
        if (res?.data?.success) {
          setQuote(res.data);
        } else {
          setError(res?.data?.error || "Could not retrieve a delivered price.");
          setManualValidationRequired(Boolean(res?.data?.manualValidationRequired));
        }
      })
      .catch((err) => {
        if (cancelled) return;
        const data = err?.response?.data || {};
        setError(data.error || err.message || "Could not retrieve a delivered price.");
        setManualValidationRequired(Boolean(data.manualValidationRequired));
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [api, targetExternalUrl, destinationRegion]);

  const productImage = quote?.productDetails?.real_images?.[0] || null;
  const showQuote = quote && !error;

  return (
    <div
      className="w-full max-w-2xl mx-auto bg-white border border-slate-200 rounded-2xl shadow-xl overflow-hidden"
      data-testid="quote-preview"
    >
      <div className="flex items-center justify-between px-5 py-3 border-b border-slate-100 bg-slate-50">
        <span className="text-xs font-semibold tracking-widest text-emerald-700 uppercase">
          Live concierge quote
        </span>
        {onClose && (
          <button
            type="button"
            onClick={onClose}
            className="text-slate-400 hover:text-slate-700 text-lg leading-none"
            aria-label="Close quote"
          >
            ×
          </button>
        )}
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-[200px_1fr] gap-4 p-5">
        <div className="aspect-square bg-slate-50 rounded-lg overflow-hidden relative">
          {loading ? (
            <div className="absolute inset-0 animate-pulse bg-slate-100" />
          ) : showQuote && productImage ? (
            <img
              src={
                imgFallbackStage === 0
                  ? productImage
                  : PLACEHOLDER_FALLBACKS[imgFallbackStage - 1] ||
                    CHEEP_LUXURY_PLACEHOLDER_DATA_URI
              }
              alt={quote.productDetails.title}
              className="w-full h-full object-contain p-3"
              loading="lazy"
              decoding="async"
              referrerPolicy="no-referrer"
              onError={() =>
                setImgFallbackStage((s) =>
                  s < PLACEHOLDER_FALLBACKS.length ? s + 1 : s
                )
              }
            />
          ) : (
            <PlaceholderImage alt="Quote pending verification" />
          )}
        </div>
        <div className="min-w-0 flex flex-col">
          {loading ? (
            <>
              <div className="h-5 bg-slate-100 rounded animate-pulse mb-2 w-3/4" />
              <div className="h-4 bg-slate-100 rounded animate-pulse mb-3 w-1/2" />
              <div className="h-8 bg-slate-100 rounded animate-pulse w-1/3 mt-auto" />
            </>
          ) : showQuote ? (
            <>
              <h3 className="text-base sm:text-lg font-semibold text-slate-900 line-clamp-3">
                {quote.productDetails.title}
              </h3>
              <p className="mt-1 text-xs uppercase tracking-wider text-slate-500">
                {quote.productDetails.marketplace}
              </p>
              <div className="mt-4 p-3 rounded-lg bg-emerald-50 border border-emerald-100">
                <div className="flex items-baseline justify-between gap-3">
                  <span className="text-xs font-medium text-emerald-700">
                    Your delivered price
                  </span>
                  <span className="text-2xl font-bold text-emerald-700">
                    {quote.pricingDetails.currency === "USD" ? "$" : ""}
                    {quote.pricingDetails.deliveredPrice}
                  </span>
                </div>
                <p className="mt-1 text-[11px] text-slate-500">
                  {quote.pricingDetails.breakdownText}
                </p>
              </div>
              {onCommit && (
                <button
                  type="button"
                  onClick={() => onCommit(quote)}
                  className="mt-4 w-full bg-orange-500 hover:bg-orange-600 text-white font-medium rounded-md py-2.5 transition-colors"
                  data-testid="commit-concierge-quote"
                >
                  Continue to concierge checkout
                </button>
              )}
            </>
          ) : (
            <>
              <h3 className="text-base sm:text-lg font-semibold text-slate-900">
                We could not auto-quote that link
              </h3>
              <p className="mt-2 text-sm text-slate-600">{error}</p>
              {manualValidationRequired && (
                <p className="mt-3 text-xs text-slate-500">
                  Paste the link into the <span className="font-medium">Submit Link</span> form and our concierge team will validate the listing within one business hour.
                </p>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
}
